import { resolveMediaUrl, toPublicProfilePictureUrl } from '../../../utils/mediaUrl';
import {
  buildScheduleEventRows,
  isUpcomingScheduledEvent,
} from '../../../utils/scheduleEventRows';
import { extractPrimaryInstrument } from '../../../utils/friendCardProfile';
import {
  buildBandStyleTags,
  formatStyleTagsForDisplay,
} from '../../../utils/styleTags';
import { formatNumber } from '../../../utils/profileEditorial';
import { resolveUserDisplayName } from '../../../utils/userIdentity';
import {
  getBandMemberMembershipStatus,
  isActiveBandMembershipStatus,
  isPendingBandMembershipStatus,
} from '../../../utils/bandMembership';

/**
 * Project a canonical band payload (as returned by `GET /api/bands/{id}` -
 * see muzone-bands-service) onto the BandPage prop schema.
 *
 * Only real backend fields are read here. Sections with no backend coverage
 * yet (shows, posts, gallery) return empty arrays so their <Section>
 * components hide cleanly; do NOT add placeholder content.
 */

function pickMonogram(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part.charAt(0)).join('').toUpperCase();
}

function pickFromCandidates(candidates) {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return resolveMediaUrl(candidate.trim());
    }
    if (candidate && typeof candidate === 'object') {
      const objectUrl = candidate.url
        || candidate.imageUrl
        || candidate.displayUrl
        || candidate.previewUrl;
      if (typeof objectUrl === 'string' && objectUrl.trim()) {
        return resolveMediaUrl(objectUrl.trim());
      }
    }
  }
  return null;
}

export function resolveBandCoverImageUrl(band) {
  return pickFromCandidates([
    band?.images?.cover,
    band?.coverImageUrl,
    band?.coverImage,
    band?.images?.banner,
    band?.images?.hero,
  ]);
}

export function resolveBandAvatarImageUrl(band) {
  return pickFromCandidates([
    band?.images?.avatar,
    band?.avatarUrl,
    band?.iconUrl,
  ]);
}

function formatYear(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return String(date.getFullYear());
}

function formatFoundedYear(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!/^\d{4}$/.test(raw)) return null;
  return raw;
}

function resolveFoundedYear(band) {
  return formatFoundedYear(band?.foundedYear) || formatYear(band?.createdAt);
}

function buildLocationLabel(band) {
  if (typeof band?.locationDisplay === 'string' && band.locationDisplay.trim()) {
    return band.locationDisplay.trim();
  }
  if (typeof band?.location === 'string' && band.location.trim()) {
    return band.location.trim();
  }
  const parts = [band?.city, band?.stateOrRegion, band?.country]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function resolveActiveMemberCount(band, activeMembers) {
  const candidates = [band?.activeMemberCount, band?.memberCount];
  for (const value of candidates) {
    if (Number.isFinite(value)) return value;
  }
  return activeMembers.length;
}

function resolveFollowerCount(band) {
  return Number.isFinite(band?.followerCount) ? band.followerCount : null;
}

function resolveMemberMainInstrument(member) {
  // Prefer the band-scoped instrument first: what the user plays *in this
  // band* trumps their profile-wide default. If the band membership doesn't
  // carry one yet, fall back to the user's profile-level primary instrument
  // (same source the Friends page uses, so band cards and friend cards stay
  // visually consistent).
  if (typeof member?.mainInstrument === 'string' && member.mainInstrument.trim()) {
    return member.mainInstrument.trim();
  }
  if (typeof member?.user?.mainInstrument === 'string' && member.user.mainInstrument.trim()) {
    return member.user.mainInstrument.trim();
  }
  const profileInstrument = extractPrimaryInstrument(member?.user?.profile)?.instrument;
  if (typeof profileInstrument === 'string' && profileInstrument.trim()) {
    return profileInstrument.trim();
  }
  return null;
}

function formatRole(member) {
  const mainInstrument = resolveMemberMainInstrument(member);
  if (mainInstrument) {
    return mainInstrument;
  }
  const role = String(member?.role || '').trim().toUpperCase();
  return role === 'OWNER' ? 'Owner' : null;
}

function resolveMemberAvatarUrl(member) {
  const profile = member?.user || {};
  const candidates = [
    profile.profilePicture,
    profile.avatarUrl,
    profile.avatar,
    member?.profilePicture,
    member?.avatarUrl,
    member?.avatar,
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string' || !candidate.trim()) {
      continue;
    }
    const publicUrl = toPublicProfilePictureUrl(candidate.trim());
    if (publicUrl) {
      return resolveMediaUrl(publicUrl);
    }
  }
  return null;
}

function adaptMember(member) {
  const profile = member?.user || {};
  const userId = member?.userId || profile.id || null;
  const membershipRole = String(member?.role || '').trim().toUpperCase();
  const membershipStatus = getBandMemberMembershipStatus(member);
  // FriendshipUserCard requires a userId to wire friendship actions and the
  // profile preview, so we drop members without one rather than rendering a
  // dead card.
  if (!userId) return null;
  return {
    id: userId,
    userId,
    username: profile.username || member?.username || undefined,
    displayName: resolveUserDisplayName(member) || undefined,
    role: formatRole(member),
    membershipRole: membershipRole || undefined,
    membershipStatus: membershipStatus || undefined,
    // Pending = an outstanding invite the user hasn't accepted yet. The grid
    // renders these dimmed with an "Invited" badge, and only for viewers who
    // are allowed to see them (owner + active members); see BandMembersSection.
    isPending: isPendingBandMembershipStatus(membershipStatus),
    mainInstrument: resolveMemberMainInstrument(member) || undefined,
    avatarUrl: resolveMemberAvatarUrl(member) || undefined,
    badge: undefined,
  };
}

function isRenderableMember(member) {
  // Keep confirmed members (treating unspecified status as active to preserve
  // existing member-card behavior) plus outstanding invites. Everything else
  // (REMOVED, left members, etc.) is dropped.
  if (member?.leftAt) return false;
  const status = getBandMemberMembershipStatus(member);
  return isActiveBandMembershipStatus(status, { treatMissingAsActive: true })
    || isPendingBandMembershipStatus(status);
}

function buildMembers(band) {
  if (!Array.isArray(band?.members)) return [];
  const renderable = band.members
    .filter(isRenderableMember)
    .map(adaptMember)
    .filter(Boolean);
  // Confirmed members first, pending invitations last, so the established
  // lineup always reads before the dimmed "Invited" cards.
  return [
    ...renderable.filter((member) => !member.isPending),
    ...renderable.filter((member) => member.isPending),
  ];
}

function buildHeroStatusItems(t, founded, locationLabel) {
  const items = [];
  if (founded) {
    items.push({
      label: t('bandPage.hero.statusEst', { year: founded, defaultValue: `Est. ${founded}` }),
    });
  }
  if (locationLabel) {
    items.push({ label: locationLabel });
  }
  return items;
}

function buildHeroFacts(t, locationLabel, founded, styleTags) {
  const facts = [];
  if (locationLabel) {
    facts.push({
      label: t('bandPage.hero.factHometown', { defaultValue: 'Hometown' }),
      value: locationLabel,
      field: 'locationDisplay',
    });
  }
  if (styleTags.length > 0) {
    facts.push({
      label: t('bandPage.hero.factStyle', { defaultValue: 'Style' }),
      value: formatStyleTagsForDisplay(styleTags),
      field: 'styleTags',
    });
  }
  if (founded) {
    facts.push({
      label: t('bandPage.hero.factFoundedShort', { defaultValue: 'Founded' }),
      value: founded,
      field: 'foundedYear',
    });
  }
  return facts;
}

function hasFiniteNumber(value) {
  return value !== null && value !== undefined && Number.isFinite(Number(value));
}

function buildHeroStats(t, { playsCount, memberCount, followerCount, founded }) {
  const unknownStatValue = t('bandPage.hero.statUnknown', { defaultValue: '-' });
  const stats = [];
  stats.push({
    value: hasFiniteNumber(playsCount) ? formatNumber(playsCount) : unknownStatValue,
    label: t('bandPage.hero.statPlays', { defaultValue: 'Plays' }),
  });
  if (memberCount != null) {
    stats.push({
      value: formatNumber(memberCount),
      label: t('bandPage.hero.statMembers', { defaultValue: 'Members' }),
    });
  }
  if (followerCount != null) {
    stats.push({
      value: formatNumber(followerCount),
      label: t('bandPage.hero.statFollowers', { defaultValue: 'Followers' }),
    });
  }
  if (founded) {
    const years = Math.max(0, new Date().getFullYear() - Number(founded));
    stats.push({
      value: formatNumber(years),
      label: t('bandPage.hero.statYearsTogether', { defaultValue: 'Years together' }),
    });
  }
  return stats;
}

export function isUpcomingBandEvent(event) {
  return isUpcomingScheduledEvent(event);
}

function buildEventRows(band, t) {
  if (!Array.isArray(band?.events)) return [];
  return buildScheduleEventRows(band.events, t);
}

function resolveMapField(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export default function adaptBandToPageData(
  band,
  t = (key, opts) => opts?.defaultValue || key,
  { playsCount = null } = {},
) {
  if (!band) return null;

  const name = band.name || '';
  const founded = resolveFoundedYear(band);
  const locationLabel = buildLocationLabel(band);
  const styleTags = buildBandStyleTags(band);
  const members = buildMembers(band);
  // Member stats count confirmed members only; pending invites are shown in
  // the grid but must not inflate the headline lineup size.
  const activeMembers = members.filter((member) => !member.isPending);
  const memberCount = resolveActiveMemberCount(band, activeMembers);
  const followerCount = resolveFollowerCount(band);

  const hero = {
    bandName: name,
    coverImageUrl: resolveBandCoverImageUrl(band),
    avatarUrl: resolveBandAvatarImageUrl(band),
    monogram: pickMonogram(name),
    status: buildHeroStatusItems(t, founded, locationLabel),
    facts: buildHeroFacts(t, locationLabel, founded, styleTags),
    stats: buildHeroStats(t, { playsCount, memberCount, followerCount, founded }),
  };

  const about = {
    title: t('bandPage.about.title', { defaultValue: 'Sound, stories, and the people behind the music.' }),
    quote: band.description || null,
    tags: styleTags,
  };

  return {
    name,
    monogram: pickMonogram(name),
    foundedYear: founded,
    hero,
    about,
    seekingMembers: resolveMapField(band.seekingMembers),
    instrumentsNeeded: resolveMapField(band.instrumentsNeeded),
    members,
    events: buildEventRows(band, t),
    posts: [],
    gallery: [],
  };
}
